import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Swords, X } from "lucide-react";
import { METRICS, MetricChart } from "@/components/PlayerComparison";

/**
 * Team-vs-team comparison — 2x2 grid of per-metric distribution overlays.
 * Mirrors PlayerComparison but with cascading League → Team selectors and
 * team distribution endpoints.
 */
export default function TeamComparison({ teamId, currentTeamName }) {
  const { user } = useAuth();
  const ignoreFriendlies = user?.ignore_friendlies ?? true;
  const [leagues, setLeagues] = useState([]);
  const [teams, setTeams] = useState([]);

  const [leagueId, setLeagueId] = useState("");
  const [otherId, setOtherId] = useState("");

  const [current, setCurrent] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loadingCmp, setLoadingCmp] = useState(false);

  // Load static lists once
  useEffect(() => {
    let alive = true;
    Promise.all([api.get("/leagues"), api.get("/teams")])
      .then(([l, t]) => { if (alive) { setLeagues(l.data); setTeams(t.data); } })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Current team distribution — refetch on ignore_friendlies toggle
  useEffect(() => {
    let alive = true;
    setCurrent(null);
    api.get(`/teams/${teamId}/distribution`)
      .then((r) => { if (alive) setCurrent({ ...r.data, _name: currentTeamName || "This team" }); })
      .catch(() => {});
    return () => { alive = false; };
  }, [teamId, currentTeamName, ignoreFriendlies]);

  // Comparison team distribution
  useEffect(() => {
    if (!otherId) { setComparison(null); return; }
    let alive = true;
    setLoadingCmp(true);
    const meta = teams.find((t) => t.id === otherId);
    api.get(`/teams/${otherId}/distribution`)
      .then((r) => { if (alive) setComparison({ ...r.data, _name: meta?.name || "Comparison" }); })
      .catch(() => { if (alive) setComparison(null); })
      .finally(() => { if (alive) setLoadingCmp(false); });
    return () => { alive = false; };
  }, [otherId, teams, ignoreFriendlies]);

  const teamsForLeague = leagueId
    ? teams.filter((t) => (t.league_ids || []).includes(leagueId) && t.id !== teamId)
    : [];

  const clearComparison = () => {
    setLeagueId(""); setOtherId(""); setComparison(null);
  };

  return (
    <section className="rounded-xl border border-border bg-card overflow-hidden" data-testid="team-comparison">
      <header className="px-6 pt-6 pb-2 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-1">Head to head</div>
          <h2 className="font-display uppercase tracking-tight text-xl">Compare with another team</h2>
        </div>
        {comparison && (
          <Button
            variant="ghost" size="sm"
            onClick={clearComparison}
            data-testid="team-comparison-clear"
            className="h-9 gap-1.5"
          >
            <X className="h-4 w-4" /> Clear comparison
          </Button>
        )}
      </header>

      {/* Selectors — stacked on mobile */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 px-6 pt-3 pb-4">
        <div className="space-y-1">
          <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">League</Label>
          <Select
            value={leagueId || "__none__"}
            onValueChange={(v) => { setLeagueId(v === "__none__" ? "" : v); setOtherId(""); }}
          >
            <SelectTrigger data-testid="team-comparison-league-select">
              <SelectValue placeholder="Pick a league" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Pick a league…</SelectItem>
              {leagues.map((l) => (
                <SelectItem key={l.id} value={l.id} data-testid={`team-comparison-league-option-${l.id}`}>
                  {l.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Team</Label>
          <Select
            value={otherId || "__none__"}
            onValueChange={(v) => setOtherId(v === "__none__" ? "" : v)}
            disabled={!leagueId}
          >
            <SelectTrigger data-testid="team-comparison-team-select">
              <SelectValue placeholder={leagueId ? (teamsForLeague.length ? "Pick a team" : "No other teams") : "Pick league first"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">
                {leagueId ? (teamsForLeague.length ? "Pick a team…" : "No other teams in this league") : "Pick league first"}
              </SelectItem>
              {teamsForLeague.map((t) => (
                <SelectItem key={t.id} value={t.id} data-testid={`team-comparison-team-option-${t.id}`}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="px-6 pb-6">
        {!comparison ? (
          <div
            className="rounded-lg border border-dashed border-border bg-muted/20 px-6 py-10 text-center"
            data-testid="team-comparison-empty"
          >
            <Swords className="h-7 w-7 mx-auto mb-3 text-muted-foreground" />
            <div className="font-display uppercase tracking-[0.18em] text-xs text-muted-foreground">
              {loadingCmp ? "Loading comparison…" : "Pick a team to compare"}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              Choose a league, then a team — their behavioural curves will overlay {currentTeamName ? <span className="text-foreground/80">{currentTeamName}'s</span> : "this team's"} curves below.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="team-comparison-grid">
            {METRICS.map((m) => (
              <MetricChart key={m.key} metric={m} current={current} comparison={comparison} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
